import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';
import { useEffect, useState } from 'react';
import CustomPagination from '../../../components/Common/CustomPagination';
import { getAdministrationDetails } from './actions';

const allHeaders = [
  'Request ID',
  'Request Date',
  'Request Type',
  'Vessel Name',
  'Vessel Code',
  'Ops ID',
  'Voyage Number',
];

const Administration = (props) => {
  const { loader = false, setLoader = () => { } } = props;
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    totalPages: 0,
    totalItems: 0,
    offset: 1,
    limit: 50
  });
  const [anchorEl, setAnchorEl] = useState(null);

  // Note: Removed S.No from allHeaders since it's hardcoded in the table. 
  // Adjusted the reduce to check index < 7 to match your original logic.
  const [visibleHeaders, setVisibleHeaders] = useState(
    allHeaders.reduce((acc, header, index) => {
      acc[header] = index < 7;
      return acc;
    }, {})
  );

  const headers = allHeaders.filter(header => visibleHeaders[header]);
  const open = Boolean(anchorEl);

  const [data, setData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    setLoader(true);
    try {
      const response = await getAdministrationDetails({
        offset: pagination.offset,
        limit: pagination.limit
      });

      // Safely handle if response exists but data is empty
      setData(response?.data || []);
      setPagination((prev) => ({
        ...prev,
        totalPages: response?.totalPages || 0,
        totalItems: response?.totalItems || 0,
      }));
    } catch (error) {
      // Handles the 404 or any other API failures gracefully
      console.warn("API Route in progress or failed. Defaulting to empty state.", error);
      setData([]);
      setPagination((prev) => ({
        ...prev,
        totalPages: 0,
        totalItems: 0,
      }));
    } finally {
      setLoading(false);
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.offset]);

  const onChangePagination = (type, key) => {
    setPagination((prev) => {
      const currentPage = prev.offset || 1;
      const limit = prev.limit || 10;

      let newOffset = currentPage;

      if (type === "first") {
        newOffset = 1;
      } else if (type === "dec" && currentPage > 1) {
        newOffset = currentPage - 1;
      } else if (type === "inc" && currentPage * limit < pagination.totalItems) {
        newOffset = currentPage + 1;
      }

      return {
        ...prev,
        offset: newOffset,
      };
    });
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleHeader = (header) => {
    setVisibleHeaders(prev => ({
      ...prev,
      [header]: !prev[header]
    }));
  };

  return (
    <div className="w-full min-h-[calc(100vh-110px)]">
      <div className="flex justify-start mb-2">
        <div className="relative">
          <button
            onClick={handleClick}
            className="h-7 px-3 bg-[#313C4C] hover:bg-[#0a2a47] text-white text-[11px] font-medium rounded-none flex items-center justify-center gap-1.5 shadow-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            Select Columns
          </button>

          {open && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={handleClose}
              />

              {/* Popover */}
              <div className="absolute z-20 mt-1 left-0 bg-white rounded-none shadow-lg border border-gray-300 min-w-48">
                <div className="h-full overflow-y-auto">
                  <div className="space-y-1.5 p-3 h-fit max-h-[50vh] overflow-y-auto scroll-thin">
                    {allHeaders.map(header => (
                      <label
                        key={header}
                        className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 text-[11px] font-medium"
                      >
                        <input
                          type="checkbox"
                          checked={visibleHeaders[header]}
                          onChange={() => handleToggleHeader(header)}
                          className="w-3.5 h-3.5 text-[#313C4C] bg-gray-100 border-gray-300 rounded-none focus:ring-[#313C4C]/20"
                        />
                        <span className="text-[11px] font-medium text-gray-700 select-none">
                          {header}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="overflow-x-auto bg-white rounded-none border border-slate-300 shadow-sm">
        {pagination?.totalItems > pagination?.limit && (
          <CustomPagination
            onChangePagination={onChangePagination}
            filters={pagination}
            totalPage={pagination.totalItems}
          />
        )}
        <Table className="universal-table w-full">
          <TableHead className="bg-[#313C4C]">
            <TableRow>
              <TableCell
                className="text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 whitespace-nowrap"
              >
                S.No
              </TableCell>
              {headers.map((header) => (
                <TableCell
                  key={header}
                  className="text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 leading-tight"
                >
                  {header}
                </TableCell>
              ))}
              <TableCell
                className="text-white !py-1 !px-1.5 text-[11px] font-medium border-r border-gray-400 whitespace-nowrap"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loader ? (
              <TableRow>
                <TableCell colSpan={headers.length + 2} align="center" className="!bg-white">
                  <Box display="flex" justifyContent="center" alignItems="center" height="440px">
                  </Box>
                </TableCell>
              </TableRow>
            ) : !loading && data.length > 0 ? (
              data.map((row, index) => (
                <TableRow
                  key={row._id || index}
                  className="h-7 hover:bg-gray-50"
                  sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}
                >
                  <TableCell className="!py-1 !px-2 text-[11px] font-medium text-center border-r border-gray-300">
                    {(pagination.offset - 1) * pagination.limit + index + 1}
                  </TableCell>
                  {visibleHeaders['Request ID'] && (
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                      {row.requestId || '-'}
                    </TableCell>
                  )}
                  {visibleHeaders['Request Date'] && (
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                      {row.requestDate || '-'}
                    </TableCell>
                  )}
                  {visibleHeaders['Request Type'] && (
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                      {row.requestType || '-'}
                    </TableCell>
                  )}
                  {visibleHeaders['Vessel Name'] && (
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                      {row.vesselName || '-'}
                    </TableCell>
                  )}
                  {visibleHeaders['Vessel Code'] && (
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                      {row.vesselCode || '-'}
                    </TableCell>
                  )}
                  {visibleHeaders['Ops ID'] && (
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                      {row.opsId || '-'}
                    </TableCell>
                  )}
                  {visibleHeaders['Voyage Number'] && (
                    <TableCell className="!py-1 !px-2 text-[11px] font-medium border-r border-gray-300">
                      {row.voyageNumber || '-'}
                    </TableCell>
                  )}
                  <TableCell className="!py-1 !px-2 text-[11px] font-medium border-gray-300 text-center">
                    -
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headers.length + 2} className="!bg-white text-center py-10">
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-2.5">
                      <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <p className="text-[11px] font-medium text-slate-700 mb-0.5">No administration data available</p>
                    <p className="text-[10px] font-medium text-slate-400">There are no administration records to display at this time.</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {pagination?.totalItems > pagination?.limit && (
          <CustomPagination
            onChangePagination={onChangePagination}
            filters={pagination}
            totalPage={pagination.totalItems}
          />
        )}
      </div>
    </div>
  );
};

export default Administration;